<template>
  <div v-if="short_term.loading || long_term.loading || cash_account.loading">
    Loading...
  </div>
  <div v-else>
    <p v-if="completed_plans.length > 1" style="font-size: 18px;">
      Use the dropdown below to review your completed plans.
    </p>
    <select 
      v-if="completed_plans.length >= 1"
      v-model="selectedPlan"
    >
      <option
        v-for="plan in completed_plans"
        :key="plan.id"
        :value="plan"
      >
        {{ plan.name }}
      </option>
    </select>
    <h3 class="completed">
      Completed
    </h3>
    <div v-if="statementMatchesPlan">
      <div class="video-and-progress">
        <div v-show="!loadingLaunchVideo[selectedPlan.id] && (selectedLaunchVideo || clientIsDiy)" class="videos-container">
          <video
            ref="launchVideoPlayer"
            class="launch-video-player"
            :style="!selectedLaunchVideo ? 'display: none' : ''"
            controls 
          >Your browser does not support the video tag.</video>
          <section
            v-if="!loadingLaunchVideo[selectedPlan.id] && !selectedLaunchVideo && selectedPlanVideo"
            :key="selectedPlanVideo.video_id"
          >
            <div
              class="wistia_responsive_padding"
              style="padding:56.25% 0 0 0;position:relative;"
            >
              <div
                class="wistia_responsive_wrapper"
                style="height:100%;left:0;position:absolute;top:0;width:100%;"
              >
                <iframe
                  :src="`https://fast.wistia.net/embed/iframe/${selectedPlanVideo.video_id}?seo=false&videoFoam=true`"
                  :title="selectedPlanVideo.title"
                  allow="autoplay; fullscreen"
                  allowtransparency="true"
                  frameborder="0"
                  scrolling="no"
                  class="wistia_embed"
                  name="wistia_embed"
                  msallowfullscreen
                  width="100%"
                  height="100%"
                />
              </div>
            </div>

            <template v-if="selectedPlanVideo.link">
              <a :href="`${ selectedPlanVideo.link }`">Download</a>
            </template>
          </section>
        </div>
        <ShortTermPlanProgress
          v-if="selectedPlan.type == 'stip'"
          :statement="selectedStatement"
        />
      </div>
      <template v-if="selectedPlan.type == 'stip'">
        <ShortTermPlanView
          v-if="selectedPlan.statement_version"
          :key="selectedPlan.id"
          :plan="selectedStatement"
          :finalized="true"
        />
        <LegacyShortTermPlanView
          v-if="!selectedPlan.statement_version"
          :key="selectedPlan.id"
          :plan="selectedStatement"
        />
      </template>
      <LongTermPlanView
        v-if="selectedPlan.type == 'ltip'"
        :key="selectedPlan.id"
        :plan="selectedStatement"
        :finalized="true"
      />
      <AllocationPoolView
        v-if="selectedPlan.type == 'cap' && selectedStatement.current.summary_data.plan.funding_type == 'allocation-pool'"
        :key="selectedPlan.id"
        :plan="selectedStatement"
        :certificates="selectedStatement.certificates"
        :finalized="true"
      />
      <CashAccountPlanView
        v-if="selectedPlan.type == 'cap' && selectedStatement.current.summary_data.plan.funding_type != 'allocation-pool'"
        :key="selectedPlan.id"
        :plan="selectedStatement"
        :certificates="selectedStatement.certificates"
        :finalized="true"
      />
    </div>
  </div>
</template>

<script setup>
// imports
import ApiManager from '@/classes/ApiManager';
import DataDirectory from '@/classes/DataDirectory';
import { ref, reactive, computed, watch, onBeforeMount, useTemplateRef } from 'vue';
import ShortTermPlanView from '@/components/pages/ShortTermPlanView.vue';
import LongTermPlanView from '@/components/pages/LongTermPlanView.vue';
import AllocationPoolView from '@/components/pages/AllocationPoolView.vue';
import CashAccountPlanView from '@/components/pages/CashAccountPlanView.vue';
import ShortTermPlanProgress from '@/components/plans/short-term/PlanProgress.vue';
// variables
const $apiManager = new ApiManager();
const $dataDirectory = new DataDirectory();
const videos = ref([]);
const launchVideos = reactive({});
const loadingLaunchVideo = reactive({});
const launchVideoPlayer = useTemplateRef('launchVideoPlayer');
const selectedPlan = ref(null);
const planStatements = reactive({});
const short_term = reactive({
  loading: false,
  plans: {},
});
const long_term = reactive({
  loading: false,
  plans: {},
});
const cash_account = reactive({
  loading: false,
  plans: {},
});
const completed_plans = computed(() => {
  const aReturn = [];
  Object.values(short_term.plans).forEach((plan) => {
    if (plan.finalized) aReturn.push({...plan, type: 'stip'});
  });
  Object.values(long_term.plans).forEach((plan) => {
    if (plan.finalized) aReturn.push({...plan, type: 'ltip'});
  });
  Object.values(cash_account.plans).forEach((plan) => {
    if (plan.finalized) aReturn.push({...plan, type: 'cap'});
  });
  return aReturn;
});
const selectedStatement = computed(() => {
  if (!planStatements[selectedPlan.value.id]) return null;
  return planStatements[selectedPlan.value.id];
});
const statementMatchesPlan = computed(() => {
  return (selectedStatement.value?.current.summary_data && (selectedStatement.value?.current.summary_data.plan_id == selectedPlan.value?.id || selectedStatement.value?.current.summary_data.plan?.id == selectedPlan.value?.id))
        || (selectedStatement.value?.current.statement_data && selectedStatement.value?.current.statement_data.plan.id == selectedPlan.value?.id)
})
const selectedPlanVideo = computed(() => {
    return videos.value.find((video) => video.plan_name == selectedPlan.value.name);
});
const selectedLaunchVideo = computed(() => {
  return launchVideos[selectedPlan.value.id];
})
const clientIsDiy = computed(() => {
  return $dataDirectory.userData.client_status === 'diy' || $dataDirectory.userData.client_status === 'DIY';
});
// watchers
watch(completed_plans, (newValue) => {
  if (newValue.length) selectedPlan.value = newValue[0];
});
watch(selectedPlan, () => {
  if (selectedLaunchVideo.value && launchVideoPlayer.value?.src != selectedLaunchVideo.value) {
    launchVideoPlayer.value.src = selectedLaunchVideo.value;
    launchVideoPlayer.value.load();
  }
});
watch(loadingLaunchVideo, (newVal) => {
  if (!newVal[selectedPlan.value.id] && selectedLaunchVideo.value && launchVideoPlayer.value?.src != selectedLaunchVideo.value) {
    launchVideoPlayer.value.src = selectedLaunchVideo.value;
    launchVideoPlayer.value.load();
  }
}, {deep: true});
// functions
function getPlanVideos() {
  $apiManager.get(`plan-videos`)
    .then((result) => {
        if(result.data?.videos) videos.value = Object.values(result.data.videos);
    })
    .catch((error) => {
        console.error(error)
    })
}
function getLaunchVideo(plan) {
  loadingLaunchVideo[plan.id] = true;
  fetch($apiManager.route("launch-video/" + plan.id), {responseType: 'arraybuffer', credentials: 'include'}).then(
    (pack) => {
      if (pack.headers.get('content-type').includes('video/')) {
        pack.arrayBuffer().then((buff) => {
          const blob = new Blob([buff], { type: pack.headers.get('content-type') });
          launchVideos[plan.id] = URL.createObjectURL(blob);
          loadingLaunchVideo[plan.id] = false;
        })
      } else {
        loadingLaunchVideo[plan.id] = false;
      }
    },
    (err) => {
      console.error(err);
      loadingLaunchVideo[plan.id] = false;
    }
  );
}
function getShortTermPlans() {
  short_term.loading = true
  $apiManager.get("plans/stip-list")
      .then((result) => {
        short_term.plans = result.data.plans;
        Object.values(result.data.plans).forEach((plan) => {
          if (plan.finalized) {
            let planWithType = {...plan, type: 'stip'};
            getStatement(planWithType);
            getLaunchVideo(planWithType);
          }
        });
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        short_term.loading = false
      })
}
function getLongTermPlans() {
  long_term.loading = true
  $apiManager.get("plans/ltip-list")
      .then((result) => {
        long_term.plans = result.data.plans;
        Object.values(result.data.plans).forEach((plan) => {
          if (plan.finalized) {
            let planWithType = {...plan, type: 'ltip'};
            getStatement(planWithType);
            getLaunchVideo(planWithType);
          }
        });
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        long_term.loading = false
      })
}
function getCashAccountPlans() {
  cash_account.loading = true
  $apiManager.get("plans/cap-list")
      .then((result) => {
        cash_account.plans = result.data.plans;
        Object.values(result.data.plans).forEach((plan) => {
          if (plan.finalized) {
            let planWithType = {...plan, type: 'cap'};
            getStatement(planWithType);
            getLaunchVideo(planWithType);
          }
        });
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        cash_account.loading = false
      })
}
function getStatement(plan) {
  $apiManager.get(`plans/${plan.type}/${plan.id}`)
    .then((result) => {
      planStatements[plan.id] = result.data;
    })
    .catch((error) => {
      console.error(error)
    })
}
onBeforeMount(() => {
  $dataDirectory.checkLogin(
    () => {},
    () => this.$router.push("/login")
  );
  getPlanVideos();
  getShortTermPlans();
  getLongTermPlans();
  getCashAccountPlans();
})
</script>

<style scoped lang="scss">
@use "../../scss/_variables.scss";
.video-and-progress {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  @media screen and (max-width: 850px){
    flex-direction: column;
    gap: 8px;
  }
}
.launch-video-player {
  max-width: 100%;
  max-height: 472px;
}
.videos-container {
  width: 100%;
  @media screen and (min-width: 851px) {
    width: 49%;
  }
}
select {
  font-size: 22px;
  font-weight: bold;
}
h3.completed {
  padding-left: 22px;
  margin-bottom: 1rem;
}
</style>